import { AllyNode } from "../../types";
import { AllyBase } from "../base";
import { AllyElementBase } from "./ally-element-base.class";

/**
 * --- This class provides part of the function matrix created by the AllyElements object. ---
 * 
 * This class creates focus traps for each layer.
 * 
 * @extends AllyElementBase
 * @see AllyElements
 * @see AllyAngular["isLayer"]
 */
export class AllyLayer extends AllyElementBase{
    /**
     * @param bridge The bridge to the parent in the hierarchical tree.
     * @see AllyBase
     */
    constructor(bridge: () => AllyBase) {
        super(bridge, (matrix) => {
            matrix[0].push((scope) => {
                if (
                    scope.self.library.angular.isLayer(scope.self) &&
                    scope.self.global.layers.findIndex((layer) => layer.rootObject === scope.self) === -1
                ) {
                    let releaseLower: (() => void) | null = null;
                    let hideLower: (() => void) | null = null;
                    if (scope.self.global.layers.length > 0) {
                        let layer = scope.self.global.layers[scope.self.global.layers.length - 1];
                        layer.previousObject = scope.self.getObjectByNode(document.activeElement as AllyNode);
                        let lower = layer.rootObject;
                        const opened = scope.self;
                        // Chrome refuses aria-hidden on an ancestor of the focused element, so focus leaves first.
                        hideLower = () => {
                            if (opened.destroyed || lower.destroyed) {
                                return;
                            }
                            if (lower.node.contains?.(document.activeElement)) {
                                (document.activeElement as HTMLElement).blur?.();
                                lower.library.a11y.focus(opened.node);
                            }
                            lower.library.a11y.setAriaHidden(lower, true);
                        };
                        const reveal = () => {
                            lower.library.a11y.setAriaHidden(lower, false);
                        };
                        lower.destroyer.addCallback(reveal);
                        releaseLower = () => lower.destroyer.removeCallback(reveal);
                    }
                    scope.self.global.layers.push({rootObject: scope.self, previousObject: null});
                    scope.self.library.angular.markLayer(scope.self);
                    let self = scope.self;
                    scope.self.destroyer.addCallback(() => {
                        releaseLower?.();
                        let index = self.global.layers.findIndex((layer) => layer.rootObject === self);
                        if (index !== -1) {
                            self.global.layers.splice(index, 1)[0];
                        }
                        if (self.global.layers.length > 0) {
                            self.library.a11y.setAriaHidden(self.global.layers[self.global.layers.length - 1].rootObject, false);
                            const previous = self.global.layers[self.global.layers.length - 1].previousObject;
                            if (previous) {
                                self.library.a11y.focus(previous.node);
                            }
                        }
                    });
                    let callback = () => {
                        hideLower?.();
                        let activeObject = self.getObjectByNode(document.activeElement as AllyNode);
                        if (activeObject && self.library.a11y.isAriaHidden(activeObject)) {
                            (document.activeElement as HTMLElement).blur && (document.activeElement as HTMLElement).blur();
                            self.library.a11y.focus(self.global.layers[self.global.layers.length - 1].rootObject.node);
                        }
                        let index = self.functionMatrix.matrix[2].indexOf(callback);
                        self.functionMatrix.matrix[2].splice(index, 1);
                    };
                    scope.self.functionMatrix.matrix[2].push(callback);
                }
            });
            matrix[0].push((scope) => {
                let self = scope.self;
                let listener = scope.self.eventListener.createEventListener(self, "focus", () => {
                    if (self.library.a11y.isAriaHidden(self)) {
                        self.node.blur && self.node.blur();
                        self.library.a11y.focus(self.global.layers[self.global.layers.length - 1].rootObject.node);
                    }
                });
                scope.self.resetFunctions.push(() => {
                    listener.destroy();
                });
            });
        }, 3);
    }
}